<template>
  <BaseModal
    :is-open="isOpen"
    title="Verified Food Available"
    size="md"
    data-testid="custom-food-migration-modal"
    @close="handleDismiss"
    @dismiss="handleDismiss"
  >
    <div v-if="currentMigration" class="migration-prompt">
      <p class="migration-prompt__body">
        <strong>{{ currentMigration.systemFoodName }}</strong> was approved as a verified system food.
        You can replace your custom food <strong>{{ currentMigration.customFoodName }}</strong> and update your old diary entries, or keep using your custom food.
      </p>

      <p
        v-if="currentMigration.customFoodIsShared"
        class="migration-prompt__warning"
        data-testid="custom-food-migration-shared-warning"
      >
        This custom food is shared with a group, so it cannot be removed. You can keep using it.
      </p>

      <p v-if="errorMessage" class="migration-prompt__error">
        {{ errorMessage }}
      </p>

      <div class="migration-prompt__actions">
        <BaseButton
          variant="outline"
          :disabled="isResolving"
          data-testid="custom-food-migration-ignore"
          @click="handleIgnore"
        >
          Keep Custom Food
        </BaseButton>
        <BaseButton
          :disabled="isResolving || currentMigration.customFoodIsShared"
          data-testid="custom-food-migration-apply"
          @click="handleApply"
        >
          Replace and Update Entries
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import BaseModal from '@/components/ui/BaseModal.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useAuthSessionStore } from '@/stores/authSessionStore';
import { useCustomFoodMigrationsStore, type SyncableCustomFoodMigrationRecord } from '@/stores/customFoodMigrationsStore';
import { useFoodItemsStore } from '@/stores/foodItemsStore';
import { useSyncStore } from '@/stores/syncStore';

const route = useRoute();
const authSessionStore = useAuthSessionStore();
const migrationsStore = useCustomFoodMigrationsStore();
const foodItemsStore = useFoodItemsStore();
const syncStore = useSyncStore();

const currentMigration = ref<SyncableCustomFoodMigrationRecord | null>(null);
const isResolving = ref(false);
const errorMessage = ref('');
const dismissedMigrationId = ref<string | null>(null);

const isRouteEligible = computed(() => {
  const routeName = typeof route.name === 'string' ? route.name : '';
  return routeName !== 'sign-in'
    && routeName !== 'oauth-callback'
    && routeName !== 'syncing'
    && routeName !== 'onboarding';
});

const isOpen = computed(() => {
  if (!isRouteEligible.value) {
    return false;
  }

  if (!currentMigration.value) {
    return false;
  }

  return dismissedMigrationId.value !== currentMigration.value.id;
});

const loadPendingMigration = async (): Promise<void> => {
  const session = await authSessionStore.loadSession();
  if (!session || !isRouteEligible.value) {
    currentMigration.value = null;
    return;
  }

  const pending = await migrationsStore.getPending();

  for (const migration of pending) {
    const customFood = await foodItemsStore.getById(migration.customFoodId);
    if (!customFood || customFood.isArchived) {
      continue;
    }

    currentMigration.value = migration;
    if (dismissedMigrationId.value !== migration.id) {
      errorMessage.value = '';
    }
    return;
  }

  currentMigration.value = null;
};

const handleDismiss = (): void => {
  dismissedMigrationId.value = currentMigration.value?.id ?? null;
};

const refreshAfterResolution = async (customFoodId: string | null): Promise<void> => {
  await syncStore.forceSync();
  if (customFoodId) {
    await migrationsStore.removeSearchDoc('custom-food', customFoodId);
  }
  dismissedMigrationId.value = null;
  await loadPendingMigration();
};

const handleApply = async (): Promise<void> => {
  if (!currentMigration.value) {
    return;
  }

  errorMessage.value = '';
  isResolving.value = true;

  try {
    const customFoodId = currentMigration.value.customFoodId;
    await migrationsStore.apply(currentMigration.value.id);
    await refreshAfterResolution(customFoodId);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to replace custom food.';
  } finally {
    isResolving.value = false;
  }
};

const handleIgnore = async (): Promise<void> => {
  if (!currentMigration.value) {
    return;
  }

  errorMessage.value = '';
  isResolving.value = true;

  try {
    await migrationsStore.ignore(currentMigration.value.id);
    dismissedMigrationId.value = null;
    await syncStore.forceSync();
    await loadPendingMigration();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to keep custom food.';
  } finally {
    isResolving.value = false;
  }
};

onMounted(async () => {
  syncStore.startListening();
  await loadPendingMigration();
});

onUnmounted(() => {
  syncStore.stopListening();
});

watch(() => route.fullPath, () => {
  void loadPendingMigration();
});

watch(() => syncStore.isSyncing, (isSyncing) => {
  if (!isSyncing) {
    void loadPendingMigration();
  }
});
</script>

<style scoped>
.migration-prompt {
  display: grid;
  gap: 0.875rem;
}

.migration-prompt__body {
  margin: 0;
  color: var(--color-text-primary);
  line-height: 1.5;
}

.migration-prompt__warning,
.migration-prompt__error {
  margin: 0;
  padding: 0.75rem 0.875rem;
  border-radius: 0.875rem;
  font-size: 0.95rem;
}

.migration-prompt__warning {
  background: color-mix(in srgb, var(--color-warning, #d97706) 10%, transparent);
  color: var(--color-text-primary);
}

.migration-prompt__error {
  background: color-mix(in srgb, var(--color-error, #dc2626) 10%, transparent);
  color: var(--color-error, #dc2626);
}

.migration-prompt__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}
</style>
